/**
 * Report API Service
 * All report endpoints under /connector/api/reports/
 */

const API_BASE_URL = 'https://et-pos.store';
const TOKEN_KEY = 'etpos_token';

const getToken = () => localStorage.getItem(TOKEN_KEY);

const buildQuery = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== null && v !== undefined && v !== '') q.append(k, v);
  });
  const s = q.toString();
  return s ? `?${s}` : '';
};

const apiFetch = async (path, params = {}) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}${path}${buildQuery(params)}`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
  });
  if (!res.ok) {
    const txt = await res.text();
    const clean = txt.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const msg = clean.length > 120 ? clean.substring(0, 120) + '...' : clean;
    throw new Error(`API error ${res.status}: ${msg || res.statusText}`);
  }
  return res.json();
};

// ─── Product Purchase Report ────────────────────────────────────────────────
export const getProductPurchaseReport = (params) =>
  apiFetch('/connector/api/reports/product-purchase', params);

export const getProductPurchaseFilterOptions = () =>
  apiFetch('/connector/api/reports/product-purchase/filter-options');

// ─── Product Sell Report ─────────────────────────────────────────────────────
export const getProductSellReport = (params) =>
  apiFetch('/connector/api/reports/product-sell', params);

export const getProductSellFilterOptions = () =>
  apiFetch('/connector/api/reports/product-sell/filter-options');

// ─── Purchase Payment Report ─────────────────────────────────────────────────
export const getPurchasePaymentReport = (params) =>
  apiFetch('/connector/api/reports/purchase-payment', params);

export const getPurchasePaymentFilterOptions = () =>
  apiFetch('/connector/api/reports/purchase-payment/filter-options');

// ─── Sell Payment Report ─────────────────────────────────────────────────────
export const getSellPaymentReport = (params) =>
  apiFetch('/connector/api/reports/sell-payment', params);

export const getSellPaymentFilterOptions = () =>
  apiFetch('/connector/api/reports/sell-payment/filter-options');

// ─── Register Report ─────────────────────────────────────────────────────────
export const getRegisterReport = (params) =>
  apiFetch('/connector/api/reports/register', params);

export const getRegisterFilterOptions = () =>
  apiFetch('/connector/api/reports/register/filter-options');

export const closeRegister = async (payload) => {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}/connector/api/cash-register/close`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    const txt = await res.text();
    const clean = txt.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const msg = clean.length > 120 ? clean.substring(0, 120) + '...' : clean;
    throw new Error(`API error ${res.status}: ${msg || res.statusText}`);
  }
  return res.json();
};

export const getRegisterDetails = (id) =>
  apiFetch(`/connector/api/cash-register/${id}/details`);

// ─── Sales Representative Report ─────────────────────────────────────────────
export const getSalesRepTotalExpense = (params) =>
  apiFetch('/connector/api/reports/sales-representative/total-expense', params);

export const getSalesRepTotalSell = (params) =>
  apiFetch('/connector/api/reports/sales-representative/total-sell', params);

export const getSalesRepTotalCommission = (params) =>
  apiFetch('/connector/api/reports/sales-representative/total-commission', params);

export const getSalesRepFilterOptions = () =>
  apiFetch('/connector/api/reports/sales-representative/filter-options');

export const getSalesRepresentativeSell = (params) =>
  apiFetch('/connector/api/reports/sales-representative/total-sell', params);

export const getSalesRepresentativeFilterOptions = () =>
  apiFetch('/connector/api/reports/sales-representative/filter-options');

// ─── Service Staff Report ─────────────────────────────────────────────────────
export const getServiceStaffReport = (params) =>
  apiFetch('/connector/api/reports/service-staff', params);

export const getServiceStaffFilterOptions = () =>
  apiFetch('/connector/api/reports/service-staff/filter-options');

// ─── Activity Log ─────────────────────────────────────────────────────────────
export const getActivityLog = (params) =>
  apiFetch('/connector/api/reports/activity-log', params);

export const getActivityLogFilterOptions = () =>
  apiFetch('/connector/api/reports/activity-log/filter-options');

// ─── Trending Products Report ─────────────────────────────────────────────────
export const getTrendingProductsReport = (params) =>
  apiFetch('/connector/api/reports/trending-products', params);

export const getTrendingProductsFilterOptions = () =>
  apiFetch('/connector/api/reports/trending-products/filter-options');

// ─── Items Report ─────────────────────────────────────────────────────────────
export const getItemsReport = (params) =>
  apiFetch('/connector/api/reports/items', params);

export const getItemsFilterOptions = () =>
  apiFetch('/connector/api/reports/items/filter-options');

// ─── Stock Report ─────────────────────────────────────────────────────────────
export const getStockReport = (params) =>
  apiFetch('/connector/api/reports/stock', params);

export const getStockFilterOptions = () =>
  apiFetch('/connector/api/reports/stock/filter-options');

// ─── Stock Adjustment Report ──────────────────────────────────────────────────
export const getStockAdjustmentSummary = (params) =>
  apiFetch('/connector/api/reports/stock-adjustment/summary', params);

export const getStockAdjustmentList = (params) =>
  apiFetch('/connector/api/reports/stock-adjustment/list', params);

// ─── Purchase & Sale Report ───────────────────────────────────────────────────
export const getPurchaseSaleReport = (params) =>
  apiFetch('/connector/api/reports/purchase-sale', params);

export const getPurchaseSaleFilterOptions = () =>
  apiFetch('/connector/api/reports/purchase-sale/filter-options');

// ─── Tax Report ───────────────────────────────────────────────────────────────
export const getTaxReportSummary = (params) =>
  apiFetch('/connector/api/reports/tax/summary', params);

export const getTaxReportInputTax = (params) =>
  apiFetch('/connector/api/reports/tax/input-tax', params);

export const getTaxReportOutputTax = (params) =>
  apiFetch('/connector/api/reports/tax/output-tax', params);

export const getTaxReportExpenseTax = (params) =>
  apiFetch('/connector/api/reports/tax/expense-tax', params);

export const getTaxDetails = (params) =>
  apiFetch('/connector/api/reports/tax/details', params);

export const getTaxReportFilterOptions = () =>
  apiFetch('/connector/api/reports/tax/filter-options');

// ─── Profit Loss Report ───────────────────────────────────────────────────────
export const getProfitLossSummary = (params) =>
  apiFetch('/connector/api/reports/profit-loss/summary', params);

export const getProfitLossFilterOptions = () =>
  apiFetch('/connector/api/reports/profit-loss/filter-options');

export const getProfitByProducts = (params) =>
  apiFetch('/connector/api/reports/profit-loss/by-products', params);

export const getProfitByCategories = (params) =>
  apiFetch('/connector/api/reports/profit-loss/by-categories', params);

export const getProfitByBrands = (params) =>
  apiFetch('/connector/api/reports/profit-loss/by-brands', params);

export const getProfitByLocations = (params) =>
  apiFetch('/connector/api/reports/profit-loss/by-locations', params);

export const getProfitByInvoice = (params) =>
  apiFetch('/connector/api/reports/profit-loss/by-invoice', params);

export const getProfitByDate = (params) =>
  apiFetch('/connector/api/reports/profit-loss/by-date', params);

export const getProfitByCustomer = (params) =>
  apiFetch('/connector/api/reports/profit-loss/by-customer', params);

export const getProfitByDay = (params) =>
  apiFetch('/connector/api/reports/profit-loss/by-day', params);

export const getStockBySellPrice = (params) =>
  apiFetch('/connector/api/reports/profit-loss/stock-by-sell-price', params);
